import { useEffect, useRef, useState } from "react";
import { calculateCertificationAmounts } from "../../../../../shared/contracts/certification-economics";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface Props {
  contractId: number;
  open: boolean;
  onClose: () => void;
  onCreated: (certificationId: number) => void;
}

export function CertificationCreateDialog({ contractId, open, onClose, onCreated }: Props) {
  const createMut = trpc.contractCertificationCreate.useMutation();
  const utils = trpc.useUtils();
  const revisionSaveMut = trpc.contractCertificationRevisionSave.useMutation();

  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [retentionAmount, setRetentionAmount] = useState("");
  const [description, setDescription] = useState("");
  const [advances, setAdvances] = useState("0");
  const [vatRate, setVatRate] = useState("");
  const [withholdingRate, setWithholdingRate] = useState("0");
  const [previewOpen, setPreviewOpen] = useState(true);
  const [savedDraft, setSavedDraft] = useState(false);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [createdRevisionId, setCreatedRevisionId] = useState<number | null>(null);
  const [revisionUpdatedAt, setRevisionUpdatedAt] = useState<string | null>(null);
  const [savedFingerprint, setSavedFingerprint] = useState("");
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const contextQuery = trpc.contractCertificationsEconomicContext.useQuery({ contractId, asOfPeriodStart: periodStart || undefined }, { enabled: open, retry: false });

  const previousOpen = useRef(false);
  const previousContractId = useRef(contractId);
  useEffect(() => {
    if (open && (!previousOpen.current || previousContractId.current !== contractId)) {
      reset();
      setCreatedId(null);
      setCreatedRevisionId(null);
      setRevisionUpdatedAt(null);
      setSavedFingerprint("");
      setSavedDraft(false);
      setPreviewOpen(true);
    } else if (!open && previousOpen.current) {
      reset();
      setCreatedId(null);
      setCreatedRevisionId(null);
      setRevisionUpdatedAt(null);
      setSavedFingerprint("");
      setSavedDraft(false);
    }
    previousOpen.current = open;
    previousContractId.current = contractId;
  }, [open, contractId]);

  function reset() {
    setPeriodStart("");
    setPeriodEnd("");
    setGrossAmount("");
    setRetentionAmount("");
    setDescription("");
    setAdvances("0");
    setVatRate("");
    setWithholdingRate("0");
    setCertificationDate(""); setCertificationType("Ordinaria"); setAdjustmentReference("");
    setPreviousCertified(""); setIssuePlace(""); setContractorName(""); setContractorTaxId("");
    setContractorAddress(""); setSubcontractorName(""); setSubcontractorTaxId(""); setSubcontractorAddress("");
  }

  const [certificationDate, setCertificationDate] = useState("");
  const [certificationType, setCertificationType] = useState("Ordinaria");
  const [adjustmentReference, setAdjustmentReference] = useState("");
  const [previousCertified, setPreviousCertified] = useState("");
  const [issuePlace, setIssuePlace] = useState("");
  const [contractorName, setContractorName] = useState("");
  const [contractorTaxId, setContractorTaxId] = useState("");
  const [contractorAddress, setContractorAddress] = useState("");
  const [subcontractorName, setSubcontractorName] = useState("");
  const [subcontractorTaxId, setSubcontractorTaxId] = useState("");
  const [subcontractorAddress, setSubcontractorAddress] = useState("");
  const preview = (() => {
    try {
      return calculateCertificationAmounts({
        workPeriod: grossAmount, advances: advances || "0", vatRatePercent: vatRate || "0",
        withholdingRatePercent: withholdingRate || "0", withholdingAmount: retentionAmount || undefined,
        previousCertified: previousCertified || String(contextQuery.data?.previouslyAccepted ?? "0"),
        awardAmount: String(contextQuery.data?.ceiling ?? "0"),
      });
    } catch { return null; }
  })();
  const advancedFields = { certificationDate: certificationDate || null, certificationType, adjustmentReference,
    previousCertified: previousCertified || String(contextQuery.data?.previouslyAccepted ?? "0"), issuePlace,
    contractorName, contractorTaxId, contractorAddress, subcontractorName, subcontractorTaxId, subcontractorAddress };
  const formFingerprint = JSON.stringify({ periodStart, periodEnd, grossAmount, retentionAmount, description, advances, vatRate, withholdingRate, ...advancedFields });
  const hasUnsavedChanges = createdId == null
    ? Boolean(periodStart || periodEnd || grossAmount || retentionAmount || description || advances !== "0" || vatRate || withholdingRate || certificationDate || adjustmentReference || previousCertified || issuePlace || contractorName || contractorTaxId || contractorAddress || subcontractorName || subcontractorTaxId || subcontractorAddress)
    : savedFingerprint !== formFingerprint;

  function requestClose() {
    if (hasUnsavedChanges) { setShowDiscardConfirm(true); return; }
    reset(); onClose();
  }

  async function handleCreate() {
    let amounts;
    try {
      amounts = calculateCertificationAmounts({
        workPeriod: grossAmount, advances: advances || "0", vatRatePercent: vatRate || "0",
        withholdingRatePercent: withholdingRate || "0", withholdingAmount: retentionAmount || undefined,
        previousCertified: previousCertified || String(contextQuery.data?.previouslyAccepted ?? "0"),
        awardAmount: String(contextQuery.data?.ceiling ?? "0"),
      });
    } catch (e: any) {
      toast.error(e?.message === "CERT_MONEY_INVALID" || e?.message === "CERT_MONEY_REQUIRED" ? "Revisa los importes: usa formato español inequívoco (por ejemplo, 1.234,56)." : "Revisa importes, IVA, retención y saldo a origen.");
      return;
    }
    const gross = Number(amounts.taxableBase);
    if (!periodStart || !periodEnd) {
      toast.error("El periodo de inicio y fin son obligatorios.");
      return;
    }
    if (!Number.isFinite(gross) || gross <= 0) {
      toast.error("La base certificada sin IVA debe ser mayor que cero.");
      return;
    }
    const retention = Number(amounts.withholdingAmount);
    if (!Number.isFinite(retention) || retention < 0) {
      toast.error("La retención no puede ser negativa.");
      return;
    }
    if (retention > gross) {
      toast.error("La retención contractual no puede superar la base certificada sin IVA.");
      return;
    }
    try {
      const payload = {
        periodStart, periodEnd, ...amounts, description: description.trim(),
        ...advancedFields,
      };
      if (createdId != null) {
        if (createdRevisionId == null || revisionUpdatedAt == null) throw new Error("CERT_REVISION_EXPECTED_VERSION_REQUIRED");
        const saved = await revisionSaveMut.mutateAsync({ certificationId: createdId, contractId, revisionId: createdRevisionId, expectedUpdatedAt: revisionUpdatedAt, payload });
        if (typeof saved?.updatedAt !== "string") throw new Error("CERT_REVISION_VERSION_MISSING");
        setRevisionUpdatedAt(saved.updatedAt);
        setSavedDraft(true);
        setSavedFingerprint(formFingerprint);
        toast.success("Cambios guardados en la revisión.");
        return;
      }
      const created = await createMut.mutateAsync({
        contractId,
        periodStart,
        periodEnd,
        grossCertifiedAmount: gross,
        retentionAmount: retention,
        description: description.trim() || null,
      });
      const savedRevision = await revisionSaveMut.mutateAsync({
        certificationId: created.id, contractId,
        revisionId: created.revisionId,
        expectedUpdatedAt: created.revisionUpdatedAt,
        payload,
      });
      if (typeof savedRevision?.updatedAt !== "string") throw new Error("CERT_REVISION_VERSION_MISSING");
      setCreatedId(created.id);
      setCreatedRevisionId(created.revisionId);
      setRevisionUpdatedAt(savedRevision.updatedAt);
      setSavedFingerprint(formFingerprint);
      setSavedDraft(true);
      toast.success("Borrador y revisión 1 guardados.");
      utils.contractCertificationsList.invalidate({ contractId });
      utils.contractCertificationsEconomicSummary.invalidate({ contractId });
      utils.contractCertificationsEconomicContext.invalidate({ contractId });
      onCreated(created.id);
    } catch (err: any) {
      const msg = err?.message ?? "Error al crear la certificación.";
      if (msg.includes("CERT_EXCEEDS_AVAILABLE_AMOUNT")) {
        toast.error("El importe supera el techo disponible del contrato.");
      } else if (msg.includes("CERT_BLOCKED_BY_STRATEGY")) {
        toast.error("La estrategia de facturación del contrato no permite certificaciones.");
      } else if (msg.includes("CERT_PERIOD_INVALID")) {
        toast.error("El período no es válido: la fecha de inicio debe ser anterior al fin.");
      } else {
        toast.error(msg);
      }
    }
  }

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) requestClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nueva certificación</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cert-period-start">Inicio del período</Label>
              <Input
                id="cert-period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cert-period-end">Fin del período</Label>
              <Input
                id="cert-period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cert-gross">Obra ejecutada en el periodo (€)</Label>
            <Input
              id="cert-gross"
              inputMode="decimal"
              placeholder="107.814,75"
              value={grossAmount}
              onChange={(e) => setGrossAmount(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="cert-advances">Anticipos / abonos (€)</Label><Input id="cert-advances" inputMode="decimal" value={advances} onChange={(e) => setAdvances(e.target.value)} /></div>
            <div className="space-y-1"><Label htmlFor="cert-vat">IVA (%)</Label><Input id="cert-vat" inputMode="decimal" value={vatRate} onChange={(e) => setVatRate(e.target.value)} /></div>
          </div>
          <div className="space-y-1"><Label htmlFor="cert-withholding-rate">Retención (%)</Label><Input id="cert-withholding-rate" inputMode="decimal" value={withholdingRate} onChange={(e) => setWithholdingRate(e.target.value)} /></div>
          <div className="space-y-2 rounded-md bg-muted/40 p-3">
            <button type="button" className="text-sm font-medium" aria-expanded={previewOpen} onClick={() => setPreviewOpen((v) => !v)}>Vista previa económica {previewOpen ? "−" : "+"}</button>
            {previewOpen && <div className="grid grid-cols-2 gap-2 text-xs"><span>Base imponible</span><span className="text-right">{preview ? Number(preview.taxableBase).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>IVA ({vatRate || "0"}%)</span><span className="text-right">{preview ? Number(preview.vatAmount).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>Retención</span><span className="text-right">{preview ? Number(preview.withholdingAmount).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>Líquido</span><span className="text-right">{preview ? Number(preview.payable).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>Acumulado a origen</span><span className="text-right">{preview ? Number(preview.cumulativeCertified).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>Pendiente</span><span className="text-right">{preview ? Number(preview.pendingToCertify).toLocaleString("es-ES", { minimumFractionDigits: 2 }) : "—"} €</span><span>Saldo disponible</span><span className="text-right">{contextQuery.data?.availableCeiling ?? "—"} {contextQuery.data?.currency ?? "€"}</span></div>}
          </div>
          {savedDraft && <p role="status" className="text-xs text-muted-foreground">Cambios guardados; la revisión permanece disponible al cerrar.</p>}

          <div className="space-y-1">
            <Label htmlFor="cert-retention">Retención contractual alternativa (€)</Label>
            <Input
              id="cert-retention"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={retentionAmount}
              onChange={(e) => setRetentionAmount(e.target.value)}
            />
          </div>
          <details className="rounded-md bg-muted/30 p-3">
            <summary className="cursor-pointer text-sm font-medium">Datos de carátula y opciones</summary>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label htmlFor="cert-date">Fecha certificación</Label><Input id="cert-date" type="date" value={certificationDate} onChange={e => setCertificationDate(e.target.value)} /></div><div className="space-y-1"><Label htmlFor="cert-type">Tipo</Label><select id="cert-type" className="h-9 w-full rounded-md border bg-background px-3" value={certificationType} onChange={e => setCertificationType(e.target.value)}><option>Ordinaria</option><option>Ampliación</option><option>Liquidación</option></select></div></div>
              {certificationType === "Ampliación" && <div className="space-y-1"><Label htmlFor="cert-adjustment">Modificación aprobada</Label><Input id="cert-adjustment" value={adjustmentReference} onChange={e => setAdjustmentReference(e.target.value)} placeholder="Referencia de la modificación contractual" /></div>}
              <div className="space-y-1"><Label htmlFor="cert-prior">Certificado anterior a origen (€)</Label><Input id="cert-prior" inputMode="decimal" value={previousCertified} onChange={e => setPreviousCertified(e.target.value)} /></div>
              <div className="grid gap-3 sm:grid-cols-2"><div className="space-y-2"><p className="text-xs font-semibold">Contratista</p><Input aria-label="Razón social contratista" value={contractorName} onChange={e => setContractorName(e.target.value)} placeholder="Razón social"/><Input aria-label="NIF contratista" value={contractorTaxId} onChange={e => setContractorTaxId(e.target.value)} placeholder="NIF/CIF"/><Input aria-label="Domicilio contratista" value={contractorAddress} onChange={e => setContractorAddress(e.target.value)} placeholder="Domicilio"/></div><div className="space-y-2"><p className="text-xs font-semibold">Subcontratista</p><Input aria-label="Razón social subcontratista" value={subcontractorName} onChange={e => setSubcontractorName(e.target.value)} placeholder="Razón social"/><Input aria-label="NIF subcontratista" value={subcontractorTaxId} onChange={e => setSubcontractorTaxId(e.target.value)} placeholder="NIF/CIF"/><Input aria-label="Domicilio subcontratista" value={subcontractorAddress} onChange={e => setSubcontractorAddress(e.target.value)} placeholder="Domicilio"/></div></div>
              <div className="space-y-1"><Label htmlFor="cert-place">Lugar de emisión</Label><Input id="cert-place" value={issuePlace} onChange={e => setIssuePlace(e.target.value)} /></div>
            </div>
          </details>
          <div className="space-y-1">
            <Label htmlFor="cert-desc">Descripción (opcional)</Label>
            <Textarea
              id="cert-desc"
              rows={2}
              placeholder="Obra certificada en este período…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={requestClose}>
            {createdId != null && !hasUnsavedChanges ? "Cerrar" : "Cancelar"}
          </Button>
          <Button onClick={handleCreate} disabled={createMut.isPending || revisionSaveMut.isPending}>
            {createMut.isPending || revisionSaveMut.isPending ? "Guardando…" : savedDraft ? "Guardar cambios" : "Guardar borrador"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>¿Descartar cambios?</DialogTitle>
          <DialogDescription>Hay cambios sin guardar. Si cierras ahora, se perderán.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDiscardConfirm(false)}>Seguir editando</Button>
          <Button variant="destructive" onClick={() => { setShowDiscardConfirm(false); reset(); onClose(); }}>Descartar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
